import React, { useEffect, useState } from "react";
import { HeroSliderFragment } from "../generated/graphql";
import useBreakPoints from "../shared/utils/useBreakPoints";
import type { HeroProps } from "../templates/types";
import type { RendererComponent } from "./types";

interface HeroRendererProps extends RendererComponent<HeroProps> {}

const HeroRenderer: React.FC<HeroRendererProps> = ({
  Component,
  heroSettings,
}) => {
  const [activeSlide, setActiveSlide] = useState(heroSettings?.[0]);
  const { isTablet } = useBreakPoints();
  const hasOverlay =
    !!activeSlide?.headingText ||
    !!activeSlide?.subHeadingText ||
    !!activeSlide?.buttons?.[0] ||
    !!activeSlide?.buttons?.[1];
  useEffect(() => {
    function changeSlide() {
      setActiveSlide((prev) => {
        const newIndex = getNextSlideIndex(prev?.id!, heroSettings);
        return heroSettings?.[newIndex];
      });
    }
    let interval: NodeJS.Timeout;
    if (heroSettings?.length! > 1) {
      interval = setInterval(changeSlide, 6000);
    }
    // cleanUp
    return () => {
      clearInterval(interval);
    };
  }, [heroSettings]);

  const handleNavigationButtons = (type: "next" | "previous") => {
    const newIndex = getNextSlideIndex(activeSlide?.id!, heroSettings, type);
    setActiveSlide(heroSettings?.[newIndex]);
  };

  if (!activeSlide) return null;

  const isButton1Exist =
    !!activeSlide?.buttons?.[0]?.text &&
    !!activeSlide?.buttons?.[0]?.link?.type;
  const isButton2Exist =
    !!activeSlide?.buttons?.[1]?.text &&
    !!activeSlide?.buttons?.[1]?.link?.type;
  let image: string | undefined | null = "";

  const getImageUrl = () => {
    return isTablet
      ? activeSlide?.mobileImage?.src || activeSlide?.image?.src
      : activeSlide?.image?.src || activeSlide?.mobileImage?.src;
  };
  const hasDesktopAndMobileImages =
    !!activeSlide?.mobileImage?.src && !!activeSlide?.image?.src;
  if (!hasDesktopAndMobileImages) image = getImageUrl();
  else {
    image = getImageUrl();
  }

  return (
    <Component
      activeSlide={activeSlide}
      heroSettings={heroSettings}
      isButton1Exist={isButton1Exist}
      isButton2Exist={isButton2Exist}
      image={image!}
      handleNavigationButtons={handleNavigationButtons}
      setActiveSlide={setActiveSlide}
      hasOverlay={hasOverlay}
    />
  );
};

export default HeroRenderer;

/**
 *
 * Function
 *
 */

function getNextSlideIndex(
  activeSlideId: string,
  heroSettings: (HeroSliderFragment | null)[] | null | undefined,
  type: "next" | "previous" = "next"
): number {
  const activeHeroIndex = heroSettings?.findIndex(
    (hero) => hero?.id === activeSlideId
  );

  if (type === "previous") {
    return (
      (activeHeroIndex! + heroSettings?.length! - 1) % heroSettings?.length!
    );
  }

  return (activeHeroIndex! + 1) % heroSettings?.length!;
}

/**
   const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    function changeSlide() {
      const newIndex = getNextSlideIndex(activeSlide?.id!, heroSettings);
      setActiveSlide(heroSettings?.[newIndex]);
    }

    if (
      heroSettings?.length! > 1 &&
      !intervalRef.current &&
      !timeoutRef.current
    ) {
      // delaying the start of the carousel would help LCP Metric in Web Vital
      timeoutRef.current = setTimeout(() => {
        intervalRef.current = setInterval(changeSlide, 6000);
      }, 15000);
    }
    // cleanUp
    return () => {
      clearInterval(intervalRef.current!);
      clearTimeout(timeoutRef.current!);
    };
  }, [activeSlide, heroSettings]);
 */
